import React from "react";
import { Upload, X, Minus, ChevronUp } from "lucide-react";

// Formats a countdown in seconds as "M:SS" (falls back to "--:--" while unknown).
const formatEta = (seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return "--:--";
  }
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Floating, dismissible/minimizable progress card shown while an
 * allowances/deductions import is in flight — mirrors the Employees import
 * progress widget (pages/(admin)employeemanagement/components/ImportProgressWidget.jsx)
 * so the two import flows feel consistent. Progress is an ESTIMATE (based on
 * row count parsed from the file) since the backend handles the whole import
 * as a single request with no real progress events — closing/minimizing this
 * widget does not cancel the in-flight import, it only stops showing it.
 */
export default function AllowanceDeductionImportProgress({
  visible,
  minimized,
  percent = 0,
  etaSeconds = null,
  fileName = "",
  language = "en",
  theme = "light",
  t,
  onMinimize,
  onExpand,
  onClose,
}) {
  if (!visible) return null;

  const isDark = theme === "dark";
  const isRTL = language === "ar";
  const clampedPercent = Math.max(0, Math.min(100, percent));

  const baseCard = {
    position: "fixed",
    bottom: 24,
    right: isRTL ? "auto" : 24,
    left: isRTL ? 24 : "auto",
    zIndex: 100000,
    background: isDark
      ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    color: isDark ? "#e6eef8" : "#0f172a",
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(148,163,184,0.2)",
    boxShadow: isDark
      ? "0 15px 40px rgba(0,0,0,0.5)"
      : "0 15px 40px rgba(15,23,42,0.15)",
    borderRadius: 14,
    animation: "adImportSlideUp 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    direction: isRTL ? "rtl" : "ltr",
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={onExpand}
        title={language === "ar" ? "عرض تقدم الاستيراد" : "Show import progress"}
        style={{
          ...baseCard,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <Upload size={16} style={{ color: "#2e7cba", flexShrink: 0 }} />
        <span>
          {language === "ar" ? "جاري الاستيراد" : "Importing"} · {Math.round(clampedPercent)}%
        </span>
        <ChevronUp size={16} style={{ opacity: 0.6 }} />
      </button>
    );
  }

  return (
    <div style={{ ...baseCard, width: 300, padding: 16 }}>
      <style>{`
        @keyframes adImportSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: isDark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Upload size={16} style={{ color: "#2e7cba" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800 }}>
            {t ? t("importingAllowancesDeductions") : "Importing allowances & deductions..."}
          </div>
          {fileName && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.6,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fileName}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onMinimize}
          title={language === "ar" ? "تصغير" : "Minimize"}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            color: isDark ? "#94a3b8" : "#64748b",
            display: "flex",
          }}
        >
          <Minus size={14} />
        </button>
        <button
          type="button"
          onClick={onClose}
          title={language === "ar" ? "إغلاق" : "Close"}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            color: isDark ? "#94a3b8" : "#64748b",
            display: "flex",
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div
        style={{
          width: "100%",
          height: 8,
          borderRadius: 999,
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${clampedPercent}%`,
            height: "100%",
            borderRadius: 999,
            background: "linear-gradient(135deg, #2e7cba 0%, #3b82f6 100%)",
            transition: "width 300ms ease",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 8,
          fontSize: 12,
          fontWeight: 600,
          opacity: 0.8,
        }}
      >
        <span>{Math.round(clampedPercent)}%</span>
        <span>
          {clampedPercent >= 100
            ? language === "ar"
              ? "على وشك الانتهاء..."
              : "Almost done..."
            : (language === "ar" ? "الوقت المتبقي تقريبًا: " : "~") +
              formatEta(etaSeconds) +
              (language === "ar" ? "" : " remaining")}
        </span>
      </div>

      <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5 }}>
        {language === "ar"
          ? "تقدير تقريبي — الاستيراد بيكمل في الخلفية حتى لو قفلت أو صغّرت النافذة"
          : "Estimated — the import keeps running in the background even if you close or minimize this"}
      </div>
    </div>
  );
}
